import { Component, Input, Optional, ViewChild } from '@angular/core';
import { WorkspaceView } from 'components/core/workspace/workspace.component';
import { ListViewComponent } from 'components/layout/list-view/list-view.component';
import { PanelComponent } from 'components/layout/panel/panel.component';
import { OrderComponent } from 'components/sale/order.component';
import { OrderModel } from 'models/generated/Entities.GormCrm.Order';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { Pipes } from 'modules/pipes';
import { DataTracking, RefreshService } from 'services/data-tracking';
import { NavigationService } from 'services/navigation.service';
import { OrderSearchFilter, OrderStatus, SalesService } from 'services/sales.service';

@Component({
	selector: 'list-orders',
	templateUrl: './list-orders.component.html',
	providers: [DataTracking, RefreshService],
	imports: [Layout, ListView, Pipes]
})
export class ListOrdersComponent {

	private _cachedOrderList: OrderModel[] = [];
	private _sellerId?: string;
	private _salesTeamsIds?: string[];
	private _companyId?: string;
	private _statusFilter?: OrderStatus;

	@Input() set sellerId(sellerId: string | undefined) {
		this._sellerId = sellerId;
		if (sellerId) this.refreshOrdersList();
	}
	@Input() set companyId(companyId: string | undefined) {
		this._companyId = companyId;
		if (companyId) this.refreshOrdersList();
	}
	@Input() set statusFilter(statusFilter: OrderStatus | undefined) {
		this._statusFilter = statusFilter;
		if (statusFilter) this.refreshOrdersList();
	}
	@Input() set salesTeamsIds(salesTeamsIds: string[] | undefined) {
		// Client-side filter only, so just update the already loaded orders
		if (this._salesTeamsIds == salesTeamsIds) return;
		this._salesTeamsIds = salesTeamsIds;
		this.setOrdersList(this._cachedOrderList);
	}

	set filter(value: OrderSearchFilter | undefined) {
		this._sellerId = value?.sellerId;
		this._companyId = value?.companyId;
		this._statusFilter = value?.status;
		this.refreshOrdersList();
	}
	get filter() {
		const filter: OrderSearchFilter = {};
		if (this._sellerId) filter.sellerId = this._sellerId;
		if (this._companyId) filter.companyId = this._companyId;
		if (this._statusFilter) filter.status = this._statusFilter;

		return filter;
	}

	@Input() showCompanyName = true;
	@Input() showSellerName = true;
	@Input() groupBy: (order: OrderModel) => string | undefined = _ => undefined;
	@Input() subGroupBy: (order: OrderModel) => string | undefined = _ => undefined;
	@Input() disabled: (order: OrderModel) => boolean = _ => false;

	orders: OrderModel[] = [];
	selectedOrder?: OrderModel;
	loading = true;

	@ViewChild('orderList') private listView?: ListViewComponent;

	constructor(
		private readonly navigation: NavigationService,
		public readonly salesService: SalesService,
		private readonly tracker: DataTracking,
		private readonly parentView: WorkspaceView,
		private readonly refreshService: RefreshService,
		@Optional() public parent?: PanelComponent) {

		if (parent) parent.refresh = refreshService;
		refreshService.onRefresh.subscribe(() => {

			// Alter existing array, instead of creating a new using filter
			let deleteIndex = -1;
			while ((deleteIndex = this.orders.findIndex(order => this.disabled(order))) >= 0) {
				this.orders.splice(deleteIndex, 1);
			}
			
		});
	}
	public refreshAvailable() {
		if (!this.refreshService.refreshable) setTimeout(() => { this.refreshService.refreshAvailable(); });
		return true;
	}

	private async refreshOrdersList() {
		if (!this.filter) return;
		if (this.filter.sellerId) this.showSellerName = false;
		if (this.filter.companyId) this.showCompanyName = false;

		this.setOrdersList(await this.salesService.getOrders(this.filter, this.tracker));
		this.loading = false;
	}

	private setOrdersList(orders: OrderModel[]) {
		this._cachedOrderList = orders;
		if (this._salesTeamsIds) orders = orders.filter(order => this._salesTeamsIds!.includes(order.salesTeamId!));
		orders.sort((a, b) => a.created == b.created ? 0 : !a.created ? 1 : !b.created ? -1 : a.created > b.created ? -1 : 1); // TODO: (BLI #19870) Using DefaultSort with GroupBy currently creates Angular errors.
		this.orders = orders;
	}

	displayOrder(order: OrderModel) {
		this.selectedOrder = order;
		this.navigation.openRelativeView(this.parentView.component!, 'list-orders', OrderComponent).then(view => {
			view.orderId = order.id;
			view.orderCompleted.subscribe(_ => this.listView?.selectNextRow());
		});
	}

	orderHasStartedPlans(order: OrderModel): boolean {
		return order.orderLines.some(orderLine => orderLine.productPlans.some(plan => plan.deliveryDate)) ?? false;
	}
}
